import {useEffect, useState} from 'react';

import classNames from 'classnames';

import {IconAvatar} from 'shells/avatar/icon-avatar';
import {CollapsibleChevron} from 'shells/card/collapsible-chevron';
import {colors} from 'shells/colors/colors';
import {Divider} from 'shells/layout/divider';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {ChangeRow} from 'shells/metrics/change-row';
import {Metric} from 'shells/metrics/metric';
import {Body} from 'shells/typography/body';

type Props = {
    isCollapsed: boolean;
    icon: React.ComponentType<any>;
    title: string;
    value: number;
    showChevron: boolean;
    delta?: {
        percentChange: string;
        timespanSuffix: string;
        changeType: 'INCREASE' | 'DECREASE' | 'CONSTANT';
    };
    isDeltaLoading?: boolean;
    statsBreakdown: {
        header: string;
        details: {
            label: string;
            value: number;
        }[];
    };
    shouldExcludeZeroes?: boolean;
};

export function CollapsibleStatBreakdown(props: Props) {
    const {shouldExcludeZeroes = false} = props;

    const [isCollapsed, setIsCollapsed] = useState<boolean>(props.isCollapsed);

    useEffect(() => {
        setIsCollapsed(props.isCollapsed);
    }, [props.isCollapsed]);

    const Icon = props.icon;

    const bottomRow = props.isDeltaLoading ? (
        <div className='pt-2'>
            <LoadingText height={15} width={120} />
        </div>
    ) : (
        props.delta && <ChangeRow {...props.delta} />
    );

    let filteredDetails = props.statsBreakdown.details;
    if (shouldExcludeZeroes) {
        filteredDetails = filteredDetails.filter((detail) => detail.value > 0);
    }
    const shouldShowStats = filteredDetails.length > 0;

    // only show the dropdown indicator if there's actually any stats to show
    const showChevron = props.showChevron && shouldShowStats;

    const headerClassName = classNames('flex justify-sb align-top', {
        'pointer-cursor': showChevron,
    });

    return (
        <div className='pad-16 full-width border-box'>
            <Stack spacing={16}>
                <div
                    className={headerClassName}
                    onClick={() => showChevron && setIsCollapsed(!isCollapsed)}
                >
                    <Stack spacing={12} isHorizontal={true}>
                        <IconAvatar
                            icon={Icon}
                            avatarSize={32}
                            iconSize={20}
                            iconColor={colors.grey}
                            backgroundColor={colors.offWhiteDk}
                            shape='square'
                        />
                        <Metric
                            title={props.title}
                            value={props.value.toLocaleString()}
                            bottomRow={bottomRow}
                            size='normal'
                        />
                    </Stack>
                    {showChevron && (
                        // wrapped with div because the parent is align-top, the chevron will rotate around the center of the parent
                        // but is positioned off-center, which looks super wack
                        <div>
                            <CollapsibleChevron isExpanded={!isCollapsed} />
                        </div>
                    )}
                </div>
                {!isCollapsed && shouldShowStats && (
                    <Stack spacing={8}>
                        <Body size='small' color='light'>
                            {props.statsBreakdown.header}
                        </Body>
                        {filteredDetails.map((detail, index) => (
                            <Stack spacing={6} key={detail.label}>
                                <div className='flex justify-sb align-center'>
                                    <Body size='small' isBold={true} color='medium'>
                                        {detail.label}
                                    </Body>
                                    <Body size='small' isBold={true} color='medium'>
                                        {detail.value.toLocaleString()}
                                    </Body>
                                </div>
                                {index !== filteredDetails.length - 1 && (
                                    <Divider variant='line--horizontal' />
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>
        </div>
    );
}
